import cx from 'classnames';
import { useState } from 'react';

import Button from '../Button';
import Container from '../Container';
import Footer from '../Footer';
import Hamburger from '../Hamburger';
import Logo from '../Logo';
import MobileNavMenu from '../MobileNavMenu';
import Text from '../Text';

import alien from './alien.svg';
import astranout from './astranout.svg';
import ufo from './ufo.svg';

import styles from './ErrorPage.module.css';
import Box, { BoxStyleProps } from '../Box';

type ErrorCode = '404' | '500' | '403';

export interface ErrorPageProps extends BoxStyleProps {
  className?: string;
  errorCode: ErrorCode;
  onActionClick?: () => void;
}

const data: Record<
  ErrorCode,
  {
    title: string;
    description: string;
    backgroundImageUrl: string;
  }
> = {
  404: {
    title: 'Not found',
    backgroundImageUrl: astranout,
    description:
      'Looks like you’re a bit lost...We can’t find this page. Maybe it never existed.',
  },
  500: {
    title: 'Servers are unreachable',
    backgroundImageUrl: ufo,
    description:
      'Our servers may have been abducted by aliens... Give us a second while we look for them.',
  },
  403: {
    title: 'Access forbidden',
    backgroundImageUrl: alien,
    description: 'Huh... Pretty sure you shouldn’t be here...',
  },
};

export default function ErrorPage({
  className,
  errorCode = '403',
  onActionClick,
  ...otherProps
}: ErrorPageProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);

  const { description, title, backgroundImageUrl } = data[errorCode];

  return (
    <Box
      className={cx(
        styles['base'],
        styles[`base--error-${errorCode}`],
        className
      )}
      style={{ backgroundImage: `url(${backgroundImageUrl})` }}
      {...otherProps}
    >
      <header className={styles['header']}>
        <Container className={styles['header-container']}>
          <Hamburger onClick={() => setMenuOpen(true)} />
        </Container>
      </header>
      <main className={styles['main']}>
        <Container>
          <div className={styles['inner']}>
            <Logo className={styles['logo']} color="full-white" />
            <Text
              as="div"
              className={styles['error-code']}
              color="white"
              weight="700"
              mt={{
                XS: '16',
                MD: '24',
              }}
            >
              {errorCode}
            </Text>
            <Text
              className={styles['title']}
              as="h1"
              color="white"
              weight="600"
              mt="8"
              mb="0"
            >
              {title}
            </Text>
            <Text
              className={styles['description']}
              as="p"
              color="royal-200"
              mt="8"
            >
              {description}
            </Text>
            {onActionClick && errorCode === '500' && (
              <Button
                className={styles['action-button']}
                variant="primary"
                size="large"
                type="button"
                mt="24"
              >
                Refresh and try again
              </Button>
            )}
          </div>
        </Container>
      </main>
      {menuOpen && <MobileNavMenu onRequestClose={() => setMenuOpen(false)} />}
      <Footer className={styles['footer']} />
    </Box>
  );
}
